import { apiFetch } from './api';

const apiUrl = import.meta.env.VITE_API_URL;

export type AcaoDesfecho = 'move' | 'lose' | 'reschedule' | 'close';

export interface DesfechoModelo {
    desfechoId?: number;
    rotulo: string;
    ordem: number;
    acao: AcaoDesfecho;
    statusDestinoId?: number | null;
    diasReagendamento?: number | null;
}

export interface ModeloTarefa {
    modeloTarefaId?: number;
    /** Etapa em que a tarefa nasce: 2, 4 ou 5. */
    statusId: number;
    titulo: string;
    tipoTarefaId?: number | null;
    /** Da biblioteca: o nome do tipo; a API acha ou cria o tipo pelo nome. */
    tipoTarefaNome?: string | null;
    prazoDiasUteis: number;
    ativo: boolean;
    desfechos: DesfechoModelo[];
}

/** O trilho ATIVO da empresa, no formato que a aba Parâmetros lê. */
export interface Trilho {
    chaveBiblioteca: string | null;
    etapas: ModeloTarefa[];
}

/** Um cartão da galeria. */
export interface ConjuntoTrilho {
    conjuntoId: number;
    nome: string;
    chaveBiblioteca: string | null;
    ativo: boolean;
    etapasComTarefa: number;
    desfechos: number;
    atualizadoEm: string;
}

/** O que o quadro abre: o cartão + o desenho + as etapas. */
export interface ConjuntoTrilhoDetalhe extends ConjuntoTrilho {
    /** JSON com a posição das caixas: {"2":{"x":72,"y":72}}. */
    layout: string | null;
    etapas: ModeloTarefa[];
}

/** O corpo que o quadro envia ao criar e ao salvar. */
export interface ConjuntoTrilhoBody {
    nome: string;
    chaveBiblioteca?: string | null;
    layout?: string | null;
    etapas: ModeloTarefa[];
}

interface ApiResponse<T> { sucesso: boolean; mensagem: string; dados: T; }

const base = `${apiUrl}/api/ModeloTarefa`;

const json = async <T,>(res: Response): Promise<ApiResponse<T>> => res.json();

export const ModeloTarefaService = {
    /** O trilho ativo — quem usa: a aba Parâmetros e a coluna "No trilho" dos Tipos. */
    async listar(): Promise<Trilho> {
        const res = await apiFetch(base);
        const r: ApiResponse<Trilho> = await res.json();
        if (!r.sucesso) throw new Error(r.mensagem || 'Erro ao carregar o trilho.');
        return r.dados;
    },

    /** A galeria. */
    async listarConjuntos(): Promise<ConjuntoTrilho[]> {
        const res = await apiFetch(`${base}/Conjuntos`);
        const r: ApiResponse<ConjuntoTrilho[]> = await res.json();
        if (!r.sucesso) throw new Error(r.mensagem || 'Erro ao carregar os trilhos.');
        return r.dados ?? [];
    },

    async buscarConjunto(id: number): Promise<ConjuntoTrilhoDetalhe> {
        const res = await apiFetch(`${base}/Conjuntos/${id}`);
        const r: ApiResponse<ConjuntoTrilhoDetalhe> = await res.json();
        if (!r.sucesso) throw new Error(r.mensagem || 'Erro ao carregar o trilho.');
        return r.dados;
    },

    async criarConjunto(body: ConjuntoTrilhoBody): Promise<ApiResponse<ConjuntoTrilhoDetalhe>> {
        const res = await apiFetch(`${base}/Conjuntos`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return json(res);
    },

    async salvarConjunto(id: number, body: ConjuntoTrilhoBody): Promise<ApiResponse<ConjuntoTrilhoDetalhe>> {
        const res = await apiFetch(`${base}/Conjuntos/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return json(res);
    },

    async duplicarConjunto(id: number): Promise<ApiResponse<ConjuntoTrilhoDetalhe>> {
        const res = await apiFetch(`${base}/Conjuntos/${id}/Duplicar`, { method: 'POST' });
        return json(res);
    },

    async ativarConjunto(id: number): Promise<ApiResponse<ConjuntoTrilhoDetalhe>> {
        const res = await apiFetch(`${base}/Conjuntos/${id}/Ativar`, { method: 'POST' });
        return json(res);
    },

    async excluirConjunto(id: number): Promise<ApiResponse<number>> {
        const res = await apiFetch(`${base}/Conjuntos/${id}`, { method: 'DELETE' });
        return json(res);
    },
};
